import { toDanishCurrencyString } from './util';

// Response-shaping logic lifted out of api.js so it can be unit tested without
// stubbing fetch. Each function takes the already-parsed JSON body that the
// corresponding api.js call would have received and returns exactly what api.js
// used to resolve with. Behaviour is intentionally a verbatim copy of the old
// inline `.then(...)` bodies, quirks included -- that means they mutate
// `body.response` in place (stamping `id`, rewriting `profilt`) rather than
// returning a fresh object.

export const transformPropertyDetails = (body: any, propertyId: string) => {
  const result: any = body.response;
  result.id = propertyId;

  const _all = {
    id: -1,
    title: 'Alle',
    ShortText: '',
    LongText: '',
    profitable: '',
    Investment: 0,
    MoneySaving: 0,
    CO2Saving: 0,
    _greenImprovementValue: 100,
    _isAllTab: true,
  };

  if (result && Array.isArray(result.profilt) && result.profilt.length > 0) {
    let profitableProfilt = result.profilt.filter(
      data => data.profitable === 'true'
    );

    let nonProfitableProfilt = result.profilt.filter(
      data => data.profitable === 'false'
    );

    const reduceProfilt =
      profitableProfilt.length > 0 ? profitableProfilt : nonProfitableProfilt;

    const total = reduceProfilt.reduce((currentTotal, improvement) => {
      const addValue = parseInt(improvement.CO2Saving * 100, 10);

      _all.Investment += parseInt(improvement.Investment, 10);
      _all.MoneySaving += parseInt(improvement.MoneySaving, 10);
      _all.CO2Saving += parseFloat(improvement.CO2Saving);

      return currentTotal + addValue;
    }, 0);

    result.profilt = result.profilt
      .map(improvement => {
        const calculatedImprovement = {
          ...improvement,
          _greenImprovementValue: Math.round(
            (parseInt(improvement.CO2Saving * 100, 10) * 100) / total
          ),
        };
        return calculatedImprovement;
      })
      .sort((a, b) =>
        a._greenImprovementValue < b._greenImprovementValue ? 1 : -1
      );

    profitableProfilt = result.profilt.filter(
      data => data.profitable === 'true'
    );
    nonProfitableProfilt = result.profilt.filter(
      data => data.profitable === 'false'
    );

    result.profilt = [...profitableProfilt, ...nonProfitableProfilt];

    const noProfitable = profitableProfilt.length === 0;

    _all.profitable = noProfitable ? 'false' : 'true';

    _all.ShortText = noProfitable
      ? 'Disse energiforbedringer anbefales kun i forbindelse med renovering'
      : 'Alle rentable forbedringer';

    const alleInitialText = noProfitable
      ? 'Udføres alle renoveringer, vil det give en årlig besparelse på '
      : `Udføres alle de rentable forbedringsforslag, vil det koste <b>${toDanishCurrencyString(
          _all.Investment,
          0
        )} kr.</b> og give en årlig besparelse på `;

    _all.LongText = `${alleInitialText}<b>${toDanishCurrencyString(
      _all.MoneySaving,
      0
    )} kr.</b> og <b>${toDanishCurrencyString(
      _all.CO2Saving,
      2
    )} ton CO2.</b>`;

    result.profilt.unshift(_all);
  }

  return result;
};

export const transformCaseDetails = (body: any) => {
  const result: any = body.response;

  const _all = {
    id: -1,
    title: 'Alle',
    ShortText: 'Alle rentable forbedringer',
    LongText: '',
    profitable: 'true',
    Investment: 0,
    MoneySaving: 0,
    CO2Saving: 0,
    _greenImprovementValue: 100,
    _isAllTab: true,
  };

  if (result && Array.isArray(result.profilt) && result.profilt.length > 0) {
    const total = result.profilt.reduce((currentTotal, improvement) => {
      const addValue = parseInt(improvement.CO2Saving * 100, 10);

      _all.Investment += parseInt(improvement.Investment, 10);
      _all.MoneySaving += parseInt(improvement.MoneySaving, 10);
      _all.CO2Saving += parseFloat(improvement.CO2Saving);

      return currentTotal + addValue;
    }, 0);

    _all.LongText = `Udføres alle de rentable forbedringsforslag, vil det koste <b>${toDanishCurrencyString(
      _all.Investment,
      0
    )} kr.</b> og give en årlig besparelse på <b>${toDanishCurrencyString(
      _all.MoneySaving,
      0
    )} kr.</b> og <b>${toDanishCurrencyString(
      _all.CO2Saving,
      2
    )} ton CO2.</b>`;

    result.profilt = result.profilt
      .map(improvement => {
        const calculatedImprovement = {
          ...improvement,
          _greenImprovementValue: Math.round(
            (parseInt(improvement.CO2Saving * 100, 10) * 100) / total
          ),
        };
        return calculatedImprovement;
      })
      .sort((a, b) =>
        a._greenImprovementValue < b._greenImprovementValue ? 1 : -1
      );

    result.profilt.unshift(_all);
  }

  return result;
};

const latGridRows = 5;
const lngGridColumns = 5;
const maxBoxElements = 1;

// Thins out a bounding-box address list so at most `maxBoxElements + 1` points
// land in any one cell of a 5x5 grid laid over the box.
export const dedupeBoundingBoxResults = (body: any, bb: any) => {
  const latGridSize = (bb.maxLat - bb.minLat) / latGridRows;
  const lngGridSize = (bb.maxLng - bb.minLng) / lngGridColumns;

  let result = [];
  if (
    body.code === '200' &&
    body.response &&
    body.response.status === 'OK' &&
    Array.isArray(body.response.addressList)
  ) {
    result = body.response.addressList.map(item => {
      const lat = parseFloat(item.lat);
      const lng = parseFloat(item.lng);

      const latBox = parseInt((lat - bb.minLat) / latGridSize, 10);
      const lngBox = parseInt((lng - bb.minLng) / lngGridSize, 10);
      return {
        ...item,
        lat,
        lng,
        boxLatLng: `${latBox}-${lngBox}`,
      };
    });

    const boxCounter = {};

    result = result.filter(item => {
      const key = item.boxLatLng;
      if (!boxCounter[key]) {
        boxCounter[key] = 1;
      } else if (boxCounter[key] <= maxBoxElements) {
        boxCounter[key] += 1;
      } else {
        return false;
      }
      return true;
    });
  }
  return result;
};
